import type { OrderIntermediatesStatus } from '../Generated/OrderIntermediatesStatus.ts'
import type { IntermediatesStateKey } from '../Generated/IntermediatesState.ts'
import type { OrderState } from './OrderState.ts'

const DAY_MS = 24 * 60 * 60 * 1000

export type IntermediatesTone = 'neutral' | 'warning' | 'danger' | 'info'

export interface IntermediatesDescription {
  state: IntermediatesStateKey
  tone: IntermediatesTone
  text: string
}

/**
 * The server resolves the state once, when the page loads; the deadline keeps running while the page stays
 * open. Only the step to EXPIRED is replayed here, since it follows from `expiresAt` alone and needs no copy
 * of the backend's warning threshold.
 */
export function effectiveIntermediatesState(status: OrderIntermediatesStatus, now: Date): IntermediatesStateKey {
  const live = status.state === 'RETAINED' || status.state === 'EXPIRES_SOON'
  if (live && status.expiresAt && now.getTime() >= Date.parse(status.expiresAt)) return 'EXPIRED'
  return status.state
}

export const intermediatesExpired = (status: OrderIntermediatesStatus | null, now: Date): boolean =>
  !!status && effectiveIntermediatesState(status, now) === 'EXPIRED'

/** The states in which the deadline is the thing to act on, so the order page shows it prominently. */
export const awaitsPostprocessing = (orderState: OrderState): boolean =>
  orderState === 'READY_FOR_POSTPROCESSING' || orderState === 'PREPROCESSED_BLOCKED'

export const formatDeadline = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

export function remainingLabel(expiresAt: string, now: Date): string {
  const days = Math.floor((Date.parse(expiresAt) - now.getTime()) / DAY_MS)
  if (days < 1) return 'less than a day left'
  return days === 1 ? '1 day left' : `${days} days left`
}

export function describeIntermediates(status: OrderIntermediatesStatus, now: Date): IntermediatesDescription {
  const state = effectiveIntermediatesState(status, now)
  switch (state) {
    case 'NOT_STARTED':
      return { state, tone: 'neutral', text: 'No preprocessing intermediates yet: nothing has been submitted to preprocessing.' }
    case 'FROZEN':
      return {
        state,
        tone: 'info',
        text: `Preprocessing intermediates are frozen and kept until unfrozen${status.frozenReason ? ` (${status.frozenReason})` : ''}.`
      }
    case 'EXPIRED': {
      const when = status.sweptAt ?? status.expiresAt
      return {
        state,
        tone: 'danger',
        text:
          `Preprocessing intermediates expired${when ? ` on ${formatDeadline(when)}` : ''}. ` +
          (status.frozen ? 'The order is frozen, but the files were deleted before the freeze took effect. ' : '') +
          'Re-run preprocessing before starting postprocessing.'
      }
    }
    case 'RETAINED':
    case 'EXPIRES_SOON':
      return {
        state,
        tone: state === 'EXPIRES_SOON' ? 'warning' : 'neutral',
        text: status.expiresAt
          ? `Preprocessing intermediates kept until ${formatDeadline(status.expiresAt)}, ${remainingLabel(status.expiresAt, now)}.`
          : 'Preprocessing intermediates are kept for now.'
      }
  }
}
